import { useId, useLayoutEffect, useRef, useState } from 'react'
import useFilmLoop from '../../hooks/useFilmLoop'
import useNearViewport from '../../hooks/useNearViewport'
import useReducedMotion from '../../hooks/useReducedMotion'
import { PRINT_PROCESS_CONTROLS, PRINT_PROCESS_I18N } from '../../data/print-process-i18n'
import { Ruler, Stations } from './chrome'
import { StageLabels } from './labels'
import { VARIANTS } from './variants'
import './film.css'
import './scene.css'
import './screen.css'
import './assembly.css'

/* ==========================================================================
   One station film: a looping, cel-shaded machine cycle on a plate, the
   cycle ruler and the stations under it. The drawing mounts a screen ahead
   of the reader; the loop plays while the film is on screen and pauses with
   its button (WCAG 2.2.2). Under prefers-reduced-motion the plate shows the
   film's still until the reader presses play. The prerendered page carries
   the empty plate with its aria summary (no drawing, no button).
   ========================================================================== */

function PauseGlyph() {
  return (
    <svg viewBox="0 0 16 16" width="16" height="16" aria-hidden="true" focusable="false">
      <path d="M4.5 3h2.5v10H4.5zM9 3h2.5v10H9z" fill="currentColor" />
    </svg>
  )
}

function PlayGlyph() {
  return (
    <svg viewBox="0 0 16 16" width="16" height="16" aria-hidden="true" focusable="false">
      <path d="M5 2.75v10.5L13.5 8z" fill="currentColor" />
    </svg>
  )
}

/** Draws the still before paint while the film is not looping. */
function usePoster(ref, show, drawPoster) {
  useLayoutEffect(() => {
    if (show && ref.current) drawPoster(ref.current)
  }, [ref, show, drawPoster])
}

/**
 * `delay` staggers the first autoplay; `labelledBy` names the figure after
 * its heading (the figcaption holds the stations, not a title).
 *
 * @param {{
 *   variant: 'pad' | 'screen' | 'assembly',
 *   lang?: 'tr' | 'en' | 'de',
 *   size?: 'plate' | 'tile',
 *   delay?: number,
 *   labelledBy?: string,
 * }} props
 */
export default function Film({ variant, lang = 'tr', size = 'plate', delay = 0, labelledBy }) {
  const config = VARIANTS[variant] ?? VARIANTS.pad
  const variantCopy = PRINT_PROCESS_I18N[variant] ?? PRINT_PROCESS_I18N.pad
  const copy = variantCopy[lang] ?? variantCopy.tr
  const controls = PRINT_PROCESS_CONTROLS[lang] ?? PRINT_PROCESS_CONTROLS.tr
  const uid = useId().replace(/[^a-zA-Z0-9_-]/g, '')
  const rootRef = useRef(null)

  const reducedMotion = useReducedMotion()
  const isPrerender = typeof window !== 'undefined' && Boolean(window.__ANKA_PRERENDER__)
  // null follows the motion preference; a press of the button overrides it.
  const [choice, setChoice] = useState(null)
  // The prerendered page carries the plate and its aria summary, not the drawing.
  const mounted = useNearViewport(rootRef) && !isPrerender
  const autoplay = !reducedMotion && !isPrerender
  const looping = mounted && !isPrerender && (choice !== null || autoplay)
  const playing = choice === null ? autoplay : choice === 'play'
  const plate = size === 'plate'

  useFilmLoop(rootRef, {
    enabled: looping,
    playing,
    duration: config.duration,
    startAt: config.startAt,
    // Pressed play on a still: no cascade, and the film continues from that
    // finished cycle (the pad film then clears its register and swaps the cap).
    delay: autoplay ? delay : 0,
    firstCycle: autoplay ? 0 : 1,
    onFrame: config.onFrame,
  })
  usePoster(rootRef, mounted && !looping, config.drawPoster)

  const { Stage } = config
  return (
    <figure
      ref={rootRef}
      className={`pp-film pp-film--${size}`}
      data-variant={variant}
      aria-labelledby={labelledBy}
      data-loop={looping ? '' : undefined}
      data-playing={looping && playing ? '' : undefined}
    >
      <div className="pp-film__plate">
        <div className="pp__stage-box">
          <svg
            className="pp__stage"
            viewBox={config.viewBox}
            preserveAspectRatio="xMidYMid meet"
            role="img"
            aria-label={copy.aria}
            focusable="false"
          >
            {mounted && <Stage id={`pp${uid}s`} />}
          </svg>
          {mounted && <StageLabels stage={copy.stage} lang={lang} register={config.register} />}
        </div>
        {!isPrerender && (
          <button
            type="button"
            className="pp-film__toggle"
            aria-label={`${playing ? controls.pause : controls.play}: ${copy.stage.register.service}`}
            onClick={() => setChoice(playing ? 'pause' : 'play')}
          >
            {playing ? <PauseGlyph /> : <PlayGlyph />}
          </button>
        )}
      </div>
      <figcaption className="pp__chrome">
        <Ruler cycle={plate ? copy.stage.cycle : null} lang={lang} beats={config.beats} />
        <Stations stations={copy.stations} detail={plate} />
      </figcaption>
    </figure>
  )
}
